import type { Service, ServiceCategory } from "@/gql/graphql";
import type { ServiceRowData } from "../types";

/**
 * Filter services by search term
 */
export const filterServicesBySearch = (
  services: ServiceRowData[],
  searchTerm: string
): ServiceRowData[] => {
  if (!searchTerm) return services;

  const lowerSearchTerm = searchTerm.toLowerCase();
  return services.filter(
    (service) =>
      service.name.toLowerCase().includes(lowerSearchTerm) ||
      service.serviceCode.toLowerCase().includes(lowerSearchTerm) ||
      service.description?.toLowerCase().includes(lowerSearchTerm)
  );
};

/**
 * Filter categories by search term (includes services match)
 */
export const filterCategoriesBySearch = (
  categories: ServiceCategory[],
  servicesByCategory: Record<string, ServiceRowData[]>,
  searchTerm: string
): ServiceCategory[] => {
  if (!searchTerm) return categories;

  const lowerSearchTerm = searchTerm.toLowerCase();
  return categories.filter((category) => {
    // Category name matches
    const categoryMatches = category.name.toLowerCase().includes(lowerSearchTerm);

    // Any service in category matches
    const servicesMatch = servicesByCategory[category.id]?.some(
      (service) =>
        service.name.toLowerCase().includes(lowerSearchTerm) ||
        service.serviceCode.toLowerCase().includes(lowerSearchTerm) ||
        service.description?.toLowerCase().includes(lowerSearchTerm)
    );

    return categoryMatches || servicesMatch;
  });
};

/**
 * Sort categories by sort order
 */
export const sortCategoriesBySortOrder = (categories: ServiceCategory[]): ServiceCategory[] => {
  return [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * Sort services by name alphabetically
 */
export const sortServicesByName = (services: ServiceRowData[]): ServiceRowData[] => {
  return [...services].sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Group services by category
 */
export const groupServicesByCategory = (
  services: Service[],
  categories: ServiceCategory[]
): Record<string, ServiceRowData[]> => {
  const sortedCategories = sortCategoriesBySortOrder(categories);

  return sortedCategories.reduce(
    (acc, category) => {
      const categoryServices = services
        .filter((service: Service) => service.category?.id === category.id)
        .map(transformServiceToRowData);

      acc[category.id] = sortServicesByName(categoryServices);
      return acc;
    },
    {} as Record<string, ServiceRowData[]>
  );
};

/**
 * Transform Service to ServiceRowData
 */
export const transformServiceToRowData = (service: Service): ServiceRowData => ({
  id: service.id,
  name: service.name,
  description: service.description ?? undefined,
  duration: service.duration,
  pricingMode: service.pricingMode,
  basePrice: service.basePrice ?? undefined,
  minPrice: service.displayPrice?.priceRange?.minPrice ?? undefined,
  maxPrice: service.displayPrice?.priceRange?.maxPrice ?? undefined,
  isActive: service.isActive,
  serviceCode: service.serviceCode,
  allowOnlineBooking: service.allowOnlineBooking,
  sortOrder: 0,
  optionGroups: service.serviceOptionGroups || [],
});

/**
 * Calculate services statistics
 */
export const calculateServicesStats = (services: Service[]) => {
  const totalServices = services.length;
  const activeServices = services.filter((s) => s.isActive).length;
  const inactiveServices = totalServices - activeServices;
  const onlineBookingServices = services.filter((s) => s.allowOnlineBooking).length;

  return {
    totalServices,
    activeServices,
    inactiveServices,
    onlineBookingServices,
    activePercentage: totalServices > 0 ? Math.round((activeServices / totalServices) * 100) : 0,
    onlineBookingPercentage:
      totalServices > 0 ? Math.round((onlineBookingServices / totalServices) * 100) : 0,
  };
};

/**
 * Calculate categories statistics
 */
export const calculateCategoriesStats = (categories: ServiceCategory[]) => {
  const totalCategories = categories.length;
  const activeCategories = categories.filter((c) => !c.isArchived).length;
  const inactiveCategories = totalCategories - activeCategories;
  const onlineBookingCategories = categories.filter((c) => c.allowOnlineBooking).length;

  return {
    totalCategories,
    activeCategories,
    inactiveCategories,
    onlineBookingCategories,
    activePercentage:
      totalCategories > 0 ? Math.round((activeCategories / totalCategories) * 100) : 0,
    onlineBookingPercentage:
      totalCategories > 0 ? Math.round((onlineBookingCategories / totalCategories) * 100) : 0,
  };
};

/**
 * Get service price display text based on pricing mode
 * @param service - The service data
 * @param currency - Currency code
 * @param t - Optional translation function. If not provided, returns English text
 */
export const getServicePriceDisplayText = (
  service: ServiceRowData,
  currency = "USD",
  t?: (key: string) => string
): string => {
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  switch (service.pricingMode) {
    case "SINGLE_PRICE":
      return service.basePrice
        ? formatPrice(service.basePrice)
        : t
          ? t("pricing.priceNotSet")
          : "Price not set";
    case "MATRIX_PRICE":
      if (service.minPrice && service.maxPrice) {
        return `${formatPrice(service.minPrice)} - ${formatPrice(service.maxPrice)}`;
      }
      return t ? t("pricing.priceRangeNotSet") : "Price range not set";
    case "NO_PRICE":
      return t ? t("pricing.quoteRequired") : "Quote required";
    default:
      return t ? t("pricing.priceNotAvailable") : "Price not available";
  }
};

/**
 * Get service duration display text
 */
export const getServiceDurationDisplayText = (duration: number): string => {
  if (duration < 60) {
    return `${duration}min`;
  }

  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  if (minutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${minutes}min`;
};

/**
 * Check if service has option groups
 */
export const hasServiceOptions = (service: ServiceRowData): boolean => {
  return !!(service.optionGroups && service.optionGroups.length > 0);
};

/**
 * Get category color styles
 */
export const getCategoryColorStyles = (color: string | null | undefined) => {
  if (!color) return { backgroundColor: "#f3f4f6", textColor: "#374151" };

  return {
    backgroundColor: `${color}20`, // 20% opacity
    borderColor: color,
    textColor: color,
  };
};

/**
 * Validate category name
 */
export const validateCategoryName = (
  name: string,
  existingCategories: ServiceCategory[],
  currentId?: string
): string | null => {
  if (!name.trim()) {
    return "Category name is required";
  }

  if (name.trim().length < 2) {
    return "Category name must be at least 2 characters";
  }

  if (name.trim().length > 50) {
    return "Category name must be less than 50 characters";
  }

  const isDuplicate = existingCategories.some(
    (cat) => cat.name.toLowerCase() === name.trim().toLowerCase() && cat.id !== currentId
  );

  if (isDuplicate) {
    return "A category with this name already exists";
  }

  return null;
};

/**
 * Validate service name and code
 */
export const validateServiceName = (
  name: string,
  existingServices: ServiceRowData[],
  currentId?: string
): string | null => {
  if (!name.trim()) {
    return "Service name is required";
  }

  if (name.trim().length < 2) {
    return "Service name must be at least 2 characters";
  }

  if (name.trim().length > 100) {
    return "Service name must be less than 100 characters";
  }

  const isDuplicate = existingServices.some(
    (service) =>
      service.name.toLowerCase() === name.trim().toLowerCase() && service.id !== currentId
  );

  if (isDuplicate) {
    return "A service with this name already exists";
  }

  return null;
};

export const validateServiceCode = (
  code: string,
  existingServices: ServiceRowData[],
  currentId?: string
): string | null => {
  if (!code.trim()) {
    return "Service code is required";
  }

  if (code.trim().length < 2) {
    return "Service code must be at least 2 characters";
  }

  if (code.trim().length > 20) {
    return "Service code must be less than 20 characters";
  }

  // Check for valid format (alphanumeric, underscore, hyphen)
  if (!/^[a-zA-Z0-9_-]+$/.test(code.trim())) {
    return "Service code can only contain letters, numbers, underscores, and hyphens";
  }

  const isDuplicate = existingServices.some(
    (service) =>
      service.serviceCode.toLowerCase() === code.trim().toLowerCase() && service.id !== currentId
  );

  if (isDuplicate) {
    return "A service with this code already exists";
  }

  return null;
};

/**
 * Generate a deterministic, ASCII-only, uppercase service code from a service name.
 * Mirrors the inference rule given to the LLM in service-agent prompts so dashboard-
 * created services follow the same convention as AI-created ones.
 *
 * Steps:
 *   1. Manual transliteration for chars NFD doesn't decompose (ß, æ, œ, ø, đ, ł).
 *   2. NFD normalize + strip combining marks (handles ä/ö/ü/é/ñ/ç/...).
 *   3. Uppercase, replace non-[A-Z0-9] runs with a single underscore, trim ends.
 *   4. Truncate to maxLength, preferring an underscore boundary in the second half.
 *
 * Examples:
 *   "Haircut Classic"          -> "HAIRCUT_CLASSIC"
 *   "Maniküre Neuset"          -> "MANIKURE_NEUSET"
 *   "Pediküre + Mit Nagellack" -> "PEDIKURE_MIT"          (truncated at boundary)
 *   "Großer Schnitt"           -> "GROSSER_SCHNITT"
 */
export const generateServiceCode = (name: string, maxLength = 20): string => {
  if (!name) return "";

  const normalized = name
    .replace(/ß/g, "ss")
    .replace(/[Ææ]/g, "ae")
    .replace(/[Œœ]/g, "oe")
    .replace(/[Øø]/g, "o")
    .replace(/[Đđ]/g, "d")
    .replace(/[Łł]/g, "l")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (normalized.length <= maxLength) return normalized;

  const window = normalized.substring(0, maxLength);
  const lastUnderscore = window.lastIndexOf("_");
  if (lastUnderscore >= Math.floor(maxLength / 2)) {
    return window.substring(0, lastUnderscore);
  }
  return window.replace(/_+$/, "");
};
